import { type TripResult, type AncillarySelectionInput, type AncillaryGroup, getPersonFullName } from '@cvo/domain';
import type { CartBookingPassenger, CartBookingProps } from '~/components/domain/Booking/CartBooking/CartBooking.model';
import type { IncludedAncillaryItem } from '~/composables/useAncillarySelection';

function buildIncludedAncillaryGroup(included: IncludedAncillaryItem[]): AncillaryGroup {
  return {
    total: { amount: '0', currency: included[0]!.price.currency },
    items: included.map((ancillary) => ({
      type: ancillary.code,
      quantity: ancillary.details.quantity,
      price: ancillary.price,
    })),
  };
}

export const useBookingSelectionStore = defineStore(
  'bookingSelection',
  () => {
    const { t } = useI18n();
    const userStore = useUserStore();
    const { formatPrice } = usePriceFormatter({ trailingZeroDisplay: 'auto' });
    const baseCart = useCart();
    const { total } = useCartCalculator(baseCart.items);
    const { displayItems } = useCartDisplayItems(baseCart.items);
    const { quotation, hasQuotation, bookingDraft, createQuotation: createQuotationBase, createBookingDraft } = useQuotation();

    const selectedOutbound = ref<TripResult | null>(null);
    const selectedInbound = ref<TripResult | null>(null);

    /**
     * Last availability (search session) ID used for quotation.
     * Stored here so the customize page can re-quote with ancillary selections.
     */
    const lastAvailabilityId = ref<string | null>(null);

    /** Trip IDs the current quotation was created for; used to skip re-quotes that would drop ancillary selections. */
    const lastQuotedOutboundId = ref<string | null>(null);
    const lastQuotedInboundId = ref<string | null>(null);

    /** Included ancillaries sourced from the availability (trip search) response. */
    const availabilityAncillaries = ref<IncludedAncillaryItem[] | null>(null);

    /**
     * Ancillaries sourced from the quotation response breakdown.
     * Falls back to included availability ancillaries so the cart is populated by default.
     */
    function getFallbackAncillaryGroups(): AncillaryGroup[] | null {
      const included = availabilityAncillaries.value?.filter((ancillary) => ancillary.included);
      if (!included?.length) return null;
      return [buildIncludedAncillaryGroup(included)];
    }

    const quotationAncillaries = computed<AncillaryGroup[] | null>(() => {
      const fromQuotation = quotation.value?.pricing?.summary?.breakdown?.ancillaries;
      return fromQuotation ? [fromQuotation] : getFallbackAncillaryGroups();
    });

    const selectedTrips = computed<TripResult[]>(() => {
      return [
        selectedOutbound.value,
        selectedInbound.value,
      ].filter((selection) => selection != null);
    });

    const passengers = computed<CartBookingPassenger[]>(() => {
      return quotation.value?.passengers.map((passenger) => ({
        id: passenger.person.id,
        name: getPersonFullName(passenger.person),
        description: t(`ui.global.passengers.type.${passenger.passengerType}`),
      })) ?? [];
    });

    const { cartItems } = useBookingCartItems(quotation, quotationAncillaries);

    watch(cartItems, (newCartItems) => {
      baseCart.setItems(newCartItems);
    });

    const cartBookingData = computed<Omit<CartBookingProps, 'quota'>>(() => {
      return {
        total: formatPrice({ amount: total.value.toString(), currency: userStore.currency }),
        // Trips stay hidden until the quotation confirms them.
        trips: hasQuotation.value ? selectedTrips.value : [],
        passengers: passengers.value,
        serviceItems: displayItems.value.filter((item) => item.group === 'ancillaries'),
        feeItems: displayItems.value.filter((item) => item.group === 'trip'),
      };
    });

    function reset() {
      bookingDraft.value = undefined;
      clearSelection();
    }

    function clearTrips() {
      selectedOutbound.value = null;
      selectedInbound.value = null;
      baseCart.removeItemsByGroup('trip');
    }

    function clearSelection() {
      baseCart.clearItems();
      quotation.value = undefined;
      selectedOutbound.value = null;
      selectedInbound.value = null;
      lastQuotedOutboundId.value = null;
      lastQuotedInboundId.value = null;
    }

    function clearInboundSelection() {
      selectedInbound.value = null;
    }

    /** Creates a quotation and records the IDs it was made for so callers can detect a no-op re-quote. */
    function createQuotation(availabilityId: string, outboundId: TripResult['id'], inboundId?: TripResult['id'], ancillaries?: AncillarySelectionInput[]) {
      lastAvailabilityId.value = availabilityId;
      lastQuotedOutboundId.value = outboundId;
      lastQuotedInboundId.value = inboundId ?? null;
      return createQuotationBase(availabilityId, outboundId, inboundId, ancillaries);
    }

    return {
      // Booking creation
      quotation,
      hasQuotation,
      bookingDraft,
      createQuotation,
      createBookingDraft,

      // Selection
      selectedOutbound,
      selectedInbound,
      selectedTrips,

      // Methods
      reset,
      clearSelection,
      clearInboundSelection,
      clearTrips,

      // Ancillaries
      availabilityAncillaries,
      quotationAncillaries,
      lastAvailabilityId,
      lastQuotedOutboundId,
      lastQuotedInboundId,

      // Cart
      total,
      cartBookingData,
      passengers,
      resetSelection: baseCart.clearItems,
      removeItem: baseCart.removeItem,
    };
  },
  {
    persist: true,
  },
);
